/**
 * ProfileStrengthCard — iter 142
 *
 * Renders the Talent Passport Profile Strength Score (0-100) as a
 * progress ring + itemized checklist. Every scored section deep-links to
 * its editor. Score updates live: whenever the parent page dispatches a
 * `passport-strength-refresh` window event, the card refetches.
 *
 * Copy rules (server-authoritative — see backend passport_strength.py):
 *   • ONLY talks about profile completeness
 *   • NEVER implies employers prefer higher scores or guaranteed outcomes
 *   • Server sends the exact microcopy string — client renders it verbatim
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, Sparkles, Circle } from "lucide-react";
import api from "@/lib/api";

const Ring = ({ value = 0, max = 100 }) => {
    const pct = Math.max(0, Math.min(100, (value / max) * 100));
    const R = 42;
    const C = 2 * Math.PI * R;
    const dash = (pct / 100) * C;
    return (
        <svg viewBox="0 0 100 100" className="w-24 h-24" data-testid="profile-strength-ring">
            <circle cx="50" cy="50" r={R} className="fill-none stroke-muted" strokeWidth="8" />
            <circle
                cx="50" cy="50" r={R}
                className="fill-none stroke-emerald-500 transition-[stroke-dashoffset] duration-500"
                strokeWidth="8" strokeLinecap="round"
                strokeDasharray={`${dash} ${C - dash}`}
                transform="rotate(-90 50 50)"
            />
            <text x="50" y="55" textAnchor="middle" className="fill-foreground font-heading font-semibold text-2xl">
                {value}
            </text>
        </svg>
    );
};

const SectionRow = ({ section }) => {
    const done = section.earned >= section.weight;
    const partial = section.earned > 0 && section.earned < section.weight;
    return (
        <li
            className="flex items-center gap-3 py-2.5 border-b border-border last:border-0"
            data-testid={`strength-section-${section.key}`}
        >
            <div className="shrink-0">
                {done ? (
                    <CheckCircle2 size={18} className="text-emerald-600" />
                ) : (
                    <Circle size={18} className={partial ? "text-amber-500" : "text-muted-foreground/50"} />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${done ? "text-muted-foreground line-through" : "text-foreground"}`}>
                    {section.label}
                </div>
                {section.sub_items && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                        {section.sub_items.map((s) => (
                            <span key={s.key} className={`mr-2 ${s.earned ? "text-emerald-600" : ""}`}>
                                {s.earned ? "✓" : "○"} {s.label}
                            </span>
                        ))}
                    </div>
                )}
            </div>
            <div className="shrink-0 text-right">
                <div className={`text-xs font-semibold ${done ? "text-emerald-600" : "text-foreground"}`}>
                    {section.earned}/{section.weight}
                </div>
                {!done && (
                    <Link
                        to={section.editor}
                        className="mt-0.5 inline-flex items-center gap-0.5 text-[11px] text-primary hover:underline"
                        data-testid={`strength-section-cta-${section.key}`}
                    >
                        {section.cta || "Complete"} <ArrowRight size={10} />
                    </Link>
                )}
            </div>
        </li>
    );
};

const ProfileStrengthCard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/professional/passport-strength");
            setData(data);
        } catch {
            /* not a talent, or not signed in — render nothing */
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        load();
        const handler = () => load();
        window.addEventListener("passport-strength-refresh", handler);
        return () => window.removeEventListener("passport-strength-refresh", handler);
    }, [load]);

    const sortedSections = useMemo(() => {
        if (!data?.sections) return [];
        // Sort by score impact — unearned & partial first (largest gap), then done.
        return [...data.sections].sort((a, b) => {
            const ga = a.weight - a.earned;
            const gb = b.weight - b.earned;
            return gb - ga;
        });
    }, [data]);

    if (loading || !data) return null;

    return (
        <div
            className="rounded-2xl border border-border bg-card p-5 sm:p-6"
            data-testid="profile-strength-card"
        >
            <div className="flex items-center gap-5">
                <Ring value={data.score} max={data.max_score} />
                <div className="flex-1 min-w-0">
                    <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase text-emerald-700 dark:text-emerald-400 tracking-wide">
                        <Sparkles size={12} /> Profile strength
                    </div>
                    <h3 className="mt-1 font-heading font-semibold text-xl text-foreground" data-testid="profile-strength-headline">
                        {/* iter-168 — Spec §7 exact copy: "Profile X% complete". The
                            earlier narrative headlines are moved below so the
                            compliance-reviewed line is the first thing a talent
                            sees. */}
                        Profile {Math.round((data.score / data.max_score) * 100)}% complete
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground" data-testid="profile-strength-cta-copy">
                        Complete your passport to appear in more employer searches.
                    </p>
                    {sortedSections.length > 0 && sortedSections[0].earned < sortedSections[0].weight && (
                        <p className="mt-1 text-xs text-muted-foreground">
                            Next: <span className="text-foreground font-medium" data-testid="profile-strength-next-item">{sortedSections[0].label}</span>
                        </p>
                    )}
                </div>
            </div>

            {data.next_actions?.length > 0 && (
                <div className="mt-5">
                    <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Complete your passport</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="profile-strength-next-actions">
                        {data.next_actions.map((a) => (
                            <Link
                                key={a.key}
                                to={a.editor}
                                className="rounded-xl border border-border p-3 hover:bg-muted/50 transition text-left"
                                data-testid={`strength-next-${a.key}`}
                            >
                                <div className="text-sm font-medium text-foreground">{a.label}</div>
                                <div className="mt-1 text-xs text-emerald-700 dark:text-emerald-400">+{a.gain} points</div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            <ul className="mt-5" data-testid="profile-strength-sections">
                {sortedSections.map((s) => <SectionRow key={s.key} section={s} />)}
            </ul>
        </div>
    );
};

export default ProfileStrengthCard;
